/**
 * Per-student auto-order row state for the My Family "Auto-Order" card.
 *
 * `describeAutoOrder` maps ONE of a student's subscriptions (or the absence of
 * any) to everything a row needs to render: a status badge tone + label, the
 * meal it fills, an optional schedule line, the link target (manage an existing
 * auto-order vs. launch the set-up wizard), and the empty-state CTA label. A
 * student can hold one subscription per mealtime (breakfast AND lunch), so the
 * card calls this once per row. Pure + navigation only — it reuses
 * `statusLabel` + `summarizeWeekdays` and never mutates.
 */

import { type FamilySubscriptionWire, subscriptionStatusBadge, summarizeWeekdays } from '@/api/family-subscriptions'
import { autoOrderStateWarning } from '@/lib/auto-reload-alert'
import { compareMealtimes, MEALTIME_LABELS } from '@/lib/mealtime'

export type AutoOrderTone = 'active' | 'paused' | 'inactive'

/**
 * A household's live auto-orders keyed by studentUserId, each list ordered by
 * meal (breakfast before lunch). Archived + cancelled rows are dropped: those
 * read as "not set up", which is the empty-state row's job. A student can hold
 * one subscription per mealtime, hence a LIST per student rather than a single
 * best row.
 */
export function liveAutoOrdersByStudent(
  subscriptions: readonly FamilySubscriptionWire[],
): Map<string, FamilySubscriptionWire[]> {
  const byStudent = new Map<string, FamilySubscriptionWire[]>()
  for (const sub of subscriptions) {
    if (sub.isArchived || sub.status === 'CANCELLED') {
      continue
    }
    byStudent.set(sub.studentUserId, [...(byStudent.get(sub.studentUserId) ?? []), sub])
  }
  for (const subs of byStudent.values()) {
    subs.sort((a, b) => compareMealtimes(a.mealtime, b.mealtime))
  }
  return byStudent
}

export interface AutoOrderRowState {
  /** A manageable subscription exists (ENABLED / PAUSED / INITIALIZED). */
  hasSubscription: boolean
  /** Drives the badge color. */
  tone: AutoOrderTone
  /** Short status label, e.g. "Active", "Paused until Mar 25", "Not set up". */
  status: string
  /** Delivery-day summary when enabled; null otherwise. */
  schedule: string | null
  /**
   * "Breakfast" / "Lunch" — the meal this auto-order fills. Null when there
   * is no subscription yet. Rows are per-mealtime, so this is what tells two
   * rows for the same child apart.
   */
  mealtimeLabel: string | null
  /**
   * Funding warning when the server says this auto-order can't be paid for.
   * Null in the healthy case. The lifecycle `status` above stays truthful —
   * an ENABLED row still reads "Active"; this is the separate fact that it
   * won't actually be ordered.
   */
  warning: string | null
  /** Detail page when a subscription exists, else the set-up wizard URL. */
  href: string
  /** "Set up" when there's no subscription; null when the whole row navigates. */
  ctaLabel: string | null
}

export function describeAutoOrder(
  subscription: FamilySubscriptionWire | null,
  studentUserId: string,
): AutoOrderRowState {
  const newHref = `/family/auto-orders/new?studentUserId=${encodeURIComponent(studentUserId)}`

  // No subscription, or a cancelled one, reads as "set it up (again)".
  if (!subscription || subscription.status === 'CANCELLED') {
    return {
      hasSubscription: false,
      tone: 'inactive',
      status: 'Not set up',
      schedule: null,
      mealtimeLabel: null,
      warning: null,
      href: newHref,
      ctaLabel: 'Set up',
    }
  }

  const href = `/family/auto-orders/${encodeURIComponent(subscription.id)}`
  // Server-derived (`GET /api/family-subscriptions`). A row that can't be
  // funded must never render the plain green "Active" chip with no hint — that
  // is the contradiction with the detail page this exists to remove, so the
  // warning also downgrades the tone.
  const warning = autoOrderStateWarning(subscription.autoReloadState)
  const base = {
    hasSubscription: true,
    href,
    ctaLabel: null,
    warning,
    mealtimeLabel: MEALTIME_LABELS[subscription.mealtime],
  } as const

  if (subscription.status === 'ENABLED') {
    return {
      ...base,
      tone: warning ? 'paused' : 'active',
      // "Active", or "Active · Ends MM/DD" when an end-of-week cancel is pending.
      status: subscriptionStatusBadge(subscription).label,
      schedule: summarizeWeekdays(subscription.picks),
    }
  }

  if (subscription.status === 'PAUSED') {
    return {
      ...base,
      tone: 'paused',
      // "Paused until MM/DD" (auto-resumes) vs "Paused indefinitely" (manual).
      status: subscriptionStatusBadge(subscription).label,
      schedule: null,
    }
  }

  // INITIALIZED — created but never enabled.
  return {
    ...base,
    tone: warning ? 'paused' : 'inactive',
    status: 'Not started',
    schedule: summarizeWeekdays(subscription.picks),
  }
}
